import { placeObjetEtTravail } from './objet-des-travaux';

/**
 * Une liste d'articles collée depuis les Notes du téléphone, Word ou Excel.
 *
 * <p>Le patron garde ses articles et ses prix (prix de vente ou prix public,
 * souvent un par client) dans ses Notes ou dans Word. Collée dans l'offre ou
 * la facture, chaque ligne donne un article : son nom, sa quantité, son unité
 * et son prix. L'assistant des travaux, fait pour des phrases, y perdait les
 * prix et prenait les mesures du nom (« 4x40 », « 8 mm ») pour des quantités.
 *
 * <p>Formes lues, le prix toujours en fin de ligne :
 * <ul>
 *   <li>« Vis inox 4x40 boîte 200   12.50 » : une pièce à 12.50 ;</li>
 *   <li>« Silicone blanc 310 ml   2 x 8.90 » : deux à 8.90 ;</li>
 *   <li>« Mèche béton 8x120   5 pce à 4.20 » : cinq pièces à 4.20 ;</li>
 *   <li>« 3x Gants de travail 7.50 », « Disque 125 mm - CHF 3.80 » ;</li>
 *   <li>un tableau de Word ou d'Excel : désignation, quantité, unité, prix
 *       (et total) dans des colonnes ;</li>
 *   <li>des blocs séparés par une ligne vide : le nom de l'article sur la
 *       première ligne, puis sa description, le prix à la fin
 *       (« Démolition ⏎ Dépose de l'ancien carrelage… 50 CHF m2 »).</li>
 * </ul>
 */

// Les unités écrites après une quantité : « 5 pce », « 12 m », « 2 sacs ».
const UNITE = new RegExp(
  '(pce|pces|pc|pcs|pièces?|pieces?|u|unités?|m|ml|m2|m²|m3|m³|kg|g|l|lt|h|heures?|' +
    'rouleaux?|rouleau|boîtes?|sacs?|paquets?|cartons?|lots?|jeux?|jeu|paires?|forfait)\\.?',
).source;

// Un montant : 12.50, 12,50, 1'250.00, 1 250,00, 12.-, 12.–, CHF 12, 12 CHF, Fr. 12.50.
const NOMBRE = "\\d{1,3}(?:['’\\u00a0 ]\\d{3})+(?:[.,]\\d{1,2})?|\\d+(?:[.,]\\d{1,2})?";
const DEVISE_AVANT = '(?:(?:CHF|SFr\\.?|Fr\\.?|EUR|€)\\s*)';
const DEVISE_APRES = '(?:\\s*(?:CHF|SFr\\.?|Fr\\.?|francs|EUR|€)|\\.[-–—]|[-–—](?=\\s*$))';
const MONTANT = '(' + DEVISE_AVANT + '?)(' + NOMBRE + ')(' + DEVISE_APRES + '?)';
// « 2 x 8.90 », « 5 pce à 4.20 », « 3 @ 2.10 ».
const PAR = new RegExp('(?:x|×|\\*|à|a|@|par)').source;

/** Lit un montant écrit à la suisse ; NaN s'il n'en est pas un. */
export function montantColle(texte) {
  const brut = String(texte ?? '')
    .replace(/(CHF|SFr\.?|Fr\.?|francs|EUR|€)/gi, '')
    .replace(/\.[-–—]$|[-–—]$/, '')
    .trim();
  if (!new RegExp('^(?:' + NOMBRE + ')$').test(brut)) return NaN;
  return Number(brut.replace(/['’\u00a0 ]/g, '').replace(',', '.'));
}

/** L'unité écrite, ramenée à celle de l'offre : « pcs », « pièces » → « pce ». */
export function uniteNormale(u) {
  const v = String(u || '')
    .toLowerCase()
    .replace(/\.$/, '');
  if (!v) return 'pce';
  if (/^(pce|pces|pc|pcs|pièces?|pieces?|u|unités?)$/.test(v)) return 'pce';
  if (v === 'm2') return 'm²';
  if (v === 'm3') return 'm³';
  if (v === 'lt') return 'l';
  if (/^heures?$/.test(v)) return 'h';
  return v;
}

/** Arrondi au centime. */
const auCentime = (x) => Math.round(x * 100) / 100;

/** Quantité lue (« 2 », « 2,5 ») ; NaN sinon. */
const quantite = (t) => Number(String(t).replace(',', '.'));

/** Vrai quand le montant porte une marque de prix : décimales, devise ou « .- ». */
const marqueDePrix = (avant, nombre, apres) => !!(avant || apres || /[.,]\d{1,2}$/.test(nombre));

/** Le nom débarrassé des séparateurs laissés avant le prix. */
const nomPropre = (t) =>
  t
    .replace(/[\s\-–—:=.…]+$/u, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

/** Puce, tiret ou numéro en début de ligne. */
const sansPuce = (t) => t.replace(/^\s*(?:[-–—•*·▪◦‣]+|\d{1,3}[.)])\s+/u, '');

/** Ligne d'en-tête, de total ou de TVA, qui n'est pas un article. */
const pasUnArticle = (t) =>
  /^(?:sous[- ]?total|total|tva|mwst|iva|vat|rabais total|net à payer)\b/i.test(t) ||
  (!/\d/.test(t) && /\b(?:article|désignation|designation|qté|quantité|prix|montant)\b/i.test(t));

/**
 * Une ligne de tableau (Word, Excel) : les colonnes séparées par des tabulations.
 */
function ligneDeTableau(cellules) {
  const nombres = [];
  const textes = [];
  let unite = '';
  cellules.forEach((c) => {
    const v = c.trim();
    if (!v) return;
    const m = montantColle(v);
    if (!Number.isNaN(m)) nombres.push(m);
    else if (!unite && new RegExp('^' + UNITE + '$', 'i').test(v) && textes.length) unite = v;
    else textes.push(v);
  });
  if (!textes.length || !nombres.length) return null;
  let q = 1;
  let p = nombres[nombres.length - 1];
  if (nombres.length >= 3) {
    // Quantité, prix unitaire, total : le total se vérifie et s'ignore.
    const [a, b, c] = nombres.slice(-3);
    if (Math.abs(a * b - c) < 0.02) [q, p] = [a, b];
    else [q, p] = [nombres[nombres.length - 2], nombres[nombres.length - 1]];
  } else if (nombres.length === 2) {
    const [a, b] = nombres;
    // Quantité puis prix ; ou prix puis total quand le total est un multiple.
    [q, p] = [a, b];
  }
  return { d: textes.join(' '), q, u: unite, p };
}

/**
 * Une ligne de texte libre : « nom   [quantité [unité] x] prix ».
 */
function ligneDeTexte(t, { batiment = false } = {}) {
  // « … 5 pce à 4.20 », « … 2 x 8.90 », avec un total « = 21.00 » éventuel.
  const avecQuantite = new RegExp(
    '^(.*?\\S)\\s+(\\d+(?:[.,]\\d+)?)\\s*(?:' +
      UNITE +
      '\\s*)?' +
      PAR +
      '\\s*' +
      MONTANT +
      '(?:\\s*=\\s*' +
      DEVISE_AVANT +
      '?(?:' +
      NOMBRE +
      ')' +
      DEVISE_APRES +
      '?)?\\s*$',
    'iu',
  );
  let m = t.match(avecQuantite);
  if (m) {
    const [, nom, q, u, avant, nombre, apres] = m;
    // « 4x40 » sans espace ni décimales est une mesure, pas une quantité.
    const colle = new RegExp('\\d' + PAR + '\\d', 'iu').test(
      t.slice(nom.length).replace(/\s/g, ''),
    );
    if (u || marqueDePrix(avant, nombre, apres) || !colle) {
      return { d: nomPropre(nom), q: quantite(q), u: u || '', p: montantColle(nombre) };
    }
  }
  // « … 12.50 x 3 » : le prix, puis la quantité.
  m = t.match(
    new RegExp('^(.*?\\S)\\s+' + MONTANT + '\\s*(?:x|×)\\s*(\\d+(?:[.,]\\d+)?)\\s*$', 'iu'),
  );
  if (m && marqueDePrix(m[2], m[3], m[4])) {
    return { d: nomPropre(m[1]), q: quantite(m[5]), u: '', p: montantColle(m[3]) };
  }
  // Prix d'un tarif, à l'unité : « … 46.00 / m2 », « … 46.- le m2 », et
  // « … 50 CHF m2 » quand la devise dit que le nombre est un prix.
  m = t.match(
    new RegExp(
      '^(.*?\\S)\\s+' + MONTANT + '\\s*((?:/|le|la|les|par|au)\\s*)?' + UNITE + '\\s*$',
      'iu',
    ),
  );
  if (m && m[1].trim() && (m[5] || m[2] || /[a-z€]/i.test(m[4]))) {
    return { d: nomPropre(m[1]), q: 1, u: m[6], p: montantColle(m[3]) };
  }
  // « Carrelage 45 m2 18.00 », « Nettoyage 1 forfait 250.- » : quantité,
  // unité de travaux, puis le prix. Au bâtiment, « ml » est le mètre
  // linéaire ; ailleurs, des millilitres, une mesure du nom (« 310 ml »).
  m = t.match(
    new RegExp(
      '^(.*?[^\\d\\s])\\s+(\\d+(?:[.,]\\d+)?)\\s*(m2|m²|m3|m³|h|heures?|forfaits?' +
        (batiment ? '|ml' : '') +
        ')\\.?\\s*(?:à|x|×|@|:|=|-)?\\s*' +
        MONTANT +
        '\\s*$',
      'iu',
    ),
  );
  if (m && m[1].trim()) {
    return {
      d: nomPropre(m[1]),
      q: quantite(m[2]),
      u: m[3].replace(/s$/i, ''),
      p: montantColle(m[5]),
    };
  }
  // « … le m2 46.00 », « … m2 à 46.00 » : l'unité avant le prix. Après un
  // nombre (« Enduit 25 kg   32.50 »), l'unité est une mesure du nom.
  m = t.match(
    new RegExp(
      '^(.*?[^\\d\\s])\\s+(?:(?:le|la|les|par|au|/)\\s*)?' +
        UNITE +
        '\\s*(?:à|:|=|-)?\\s*' +
        MONTANT +
        '\\s*$',
      'iu',
    ),
  );
  // « Évacuation gravats forfait 450 » : après « forfait », le nombre est
  // un prix, même sans décimales.
  if (m && m[1].trim() && (marqueDePrix(m[3], m[4], m[5]) || /^forfait/i.test(m[2]))) {
    return { d: nomPropre(m[1]), q: 1, u: m[2], p: montantColle(m[4]) };
  }
  // « … 12.50 » : le prix seul en fin de ligne.
  m = t.match(new RegExp('^(.*?)(\\s{2,}|\\s*[-–—:=…]+\\s*|\\s+)' + MONTANT + '\\s*$', 'u'));
  if (m) {
    const [, nom, sep, avant, nombre, apres] = m;
    // Un entier collé au nom sans séparateur (« boîte 200 ») reste au nom.
    const separe = sep.trim() !== '' || sep.length >= 2;
    if (nom.trim() && (separe || marqueDePrix(avant, nombre, apres))) {
      let d = nomPropre(nom);
      let q = 1;
      let u = '';
      // « 3x Gants de travail », « 5 pce Vis » : la quantité en tête.
      const tete = d.match(
        new RegExp(
          '^(\\d+(?:[.,]\\d+)?)\\s*(?:(?:x|×)\\s+|(?:x|×)(?=\\D)|' + UNITE + '\\s+)(.+)$',
          'iu',
        ),
      );
      if (tete) {
        q = quantite(tete[1]);
        u = tete[2] || '';
        d = tete[3].trim();
      }
      return { d, q, u, p: montantColle(nombre) };
    }
  }
  return null;
}

/**
 * Les articles d'une liste collée.
 *
 * @param {string} texte la liste, une ligne par article
 * @param {object} [options] options
 * @param {'ht'|'ttc'} [options.prix] les prix collés sont hors taxe (prix de
 *   vente) ou TVA comprise (prix public) ; « ht » par défaut
 * @param {number} [options.tva] taux de TVA en %, pour les prix TVA comprise
 * @param {boolean} [options.batiment] prestations du bâtiment : de quoi il
 *   s'agit en désignation, ce qu'on fait en description (objet-des-travaux.js)
 * @returns {{d: string, sd?: string, q: number, u: string, p: number, sansPrix?: boolean}[]}
 *   les lignes, prix unitaire hors taxe ; `sd` la description d'un article
 *   écrit en bloc ; `sansPrix` quand aucun prix n'a été lu
 */
export function articlesColles(texte, { prix = 'ht', tva = 0, batiment = false } = {}) {
  const diviseur = prix === 'ttc' ? 1 + (+tva || 0) / 100 : 1;
  const lignes = String(texte || '')
    .replace(/\r\n?/g, '\n')
    .split('\n');
  const article = (brute) => {
    const t = sansPuce(brute.replace(/\u00a0/g, ' ')).trim();
    if (!t || pasUnArticle(t)) return null;
    const cellules = brute.split('\t').filter((c) => c.trim());
    const lu = cellules.length >= 2 ? ligneDeTableau(cellules) : ligneDeTexte(t, { batiment });
    if (!lu || !lu.d || !(lu.q > 0) || Number.isNaN(lu.p)) {
      return { d: nomPropre(t), q: 1, u: 'pce', p: 0, sansPrix: true };
    }
    return { ...lu, u: uniteNormale(lu.u), p: auCentime(lu.p / diviseur) };
  };
  // Les groupes de lignes séparés par une ligne vide.
  const groupes = [[]];
  lignes.forEach((l) => {
    if (l.trim()) groupes[groupes.length - 1].push(l);
    else if (groupes[groupes.length - 1].length) groupes.push([]);
  });
  return groupes
    .flatMap((g) => {
      const bloc = articleEnBloc(g);
      if (!bloc) return g.map(article);
      if (bloc.sansPrix) return [bloc];
      return [{ ...bloc, u: uniteNormale(bloc.u), p: auCentime(bloc.p / diviseur) }];
    })
    .filter(Boolean)
    .map((a) => (batiment ? { ...a, ...placeObjetEtTravail(a) } : a));
}

/** Vrai quand la ligne porte un prix lisible. */
const aUnPrix = (ligne) => {
  const t = sansPuce(ligne.replace(/\u00a0/g, ' ')).trim();
  const lu = t.includes('\t') ? null : ligneDeTexte(t);
  return !!lu && lu.q > 0 && !Number.isNaN(lu.p);
};

/**
 * Un article écrit en bloc : son nom sur la première ligne, sa description
 * sur les suivantes, le prix (« 120 CHF », « 50 CHF m2 ») à la fin de la
 * dernière. Sans prix nulle part, l'article garde son nom et sa description.
 *
 * @param {string[]} groupe les lignes du bloc, sans ligne vide
 * @returns {object|null} l'article, ou null quand les lignes sont une liste
 */
function articleEnBloc(groupe) {
  if (groupe.length < 2 || groupe.some((l) => l.includes('\t'))) return null;
  const [titre, ...suite] = groupe.map((l) => l.replace(/\u00a0/g, ' ').trim());
  // Une liste d'articles chiffrés, avec ou sans titre au-dessus.
  if (aUnPrix(titre) || suite.slice(0, -1).some(aUnPrix) || pasUnArticle(titre)) return null;
  const d = nomPropre(sansPuce(titre));
  const derniere = suite[suite.length - 1];
  const avant = suite.slice(0, -1);
  // Le prix seul sur la dernière ligne (« 50 CHF m2 », « 12 m2 x 45 ») :
  // toute la description est au-dessus.
  const prixSeul = /^(?:CHF|SFr\.?|Fr\.?|EUR|€)?\s*\d/i.test(derniere)
    ? ligneDeTexte('X ' + derniere)
    : null;
  if (prixSeul && prixSeul.d === 'X' && prixSeul.q > 0 && !Number.isNaN(prixSeul.p)) {
    return {
      d,
      sd: avant.join('\n'),
      q: prixSeul.q,
      u: prixSeul.u || 'forfait',
      p: prixSeul.p,
      qDefaut: prixSeul.q === 1,
    };
  }
  const lu = aUnPrix(derniere) ? ligneDeTexte(sansPuce(derniere)) : null;
  if (!lu) {
    return { d, sd: suite.join('\n'), q: 1, u: 'forfait', p: 0, sansPrix: true, qDefaut: true };
  }
  // La description garde son point final, enlevé avec le prix.
  const fin = lu.d + (derniere.slice(lu.d.length).trimStart().startsWith('.') ? '.' : '');
  // « 50 CHF m2 » est un prix à l'unité : la quantité 1 n'est pas écrite (qDefaut),
  // le stock ne doit pas en recevoir une.
  return {
    d,
    sd: [...avant, fin].join('\n'),
    q: lu.q,
    u: lu.u || 'forfait',
    p: lu.p,
    qDefaut: lu.q === 1,
  };
}
